import { endpoint } from '@/common/constants'
import resource from '@/api/resource'

export const PermissionName = {
  // Core menu permissions
  bot_general_view: 'bot_general_view',
  campaign_general_view: 'campaign_general_view',
  dashboard_omnichannel_view: 'dashboard_omnichannel_view',
  inbox_general_view: 'inbox_general_view',
  resources_general_view: 'resources_general_view',
  settings_general_view: 'settings_general_view',
  settings_integrations_view: 'settings_integrations_view',
  report_omnichannel_view: 'report_omnichannel_view',
  settings_scorecard_view: 'settings_scorecard_view',
  settings_callintegrations_view: 'settings_callintegrations_view',

  // Additional permissions
  callcenter_general_view: 'callcenter_general_view',
  inbox_assignagent_manage: 'inbox_assignagent_manage',
  inbox_takeover_manage: 'inbox_takeover_manage',
  inbox_scorecard_manage: 'inbox_scorecard_manage',
  inbox_scorecard_view: 'inbox_scorecard_view',
}

// Helper function to transform permissions to map
const transformPermissionsToMap = (permissions) => {
  if (!Array.isArray(permissions)) {
    return {}
  }

  return permissions.reduce((acc, permission) => {
    // Handle both string format and object format
    const permissionName =
      typeof permission === 'string'
        ? permission
        : permission.permission_name || permission.name

    if (permissionName) {
      acc[permissionName] = true
    }
    return acc
  }, {})
}

// State
export const state = () => ({
  isUseUsmanPermissions: undefined, // Changed: undefined until initialized from API
  usmanPermissionsMap: {},
  isLoading: false,
  isInitialized: false, // Added: track if API has been called
  userRole: null,
  _initPromise: null, // Added: track ongoing initialization
})

// Getters
export const getters = {
  isHasPermission: (state) => (permissionCode) => {
    if (!state.isUseUsmanPermissions) {
      return false
    }
    return !!state.usmanPermissionsMap[permissionCode]
  },

  // Role grouping helpers
  isLimitedAccessRole: (state, getters, rootState, rootGetters) => {
    const role = rootState.auth?.user?.role || state.userRole
    return role === 'agent' || role === 'member'
  },

  isFullAccessRole: (state, getters, rootState, rootGetters) => {
    const role = rootState.auth?.user?.role || state.userRole
    return (
      role === 'owner' ||
      role === 'supervisor' ||
      role === 'admin' ||
      role === 'super_admin'
    )
  },
}

// Mutations
export const mutations = {
  SET_USMAN_PERMISSIONS(state, permissions) {
    state.usmanPermissionsMap = transformPermissionsToMap(permissions)
  },
  SET_USE_USMAN_PERMISSIONS(state, useUsman) {
    state.isUseUsmanPermissions = useUsman
    state.isInitialized = true // Mark as initialized when API response is received
  },
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading
  },
  SET_USER_ROLE(state, role) {
    state.userRole = role
  },
  SET_INIT_PROMISE(state, promise) {
    state._initPromise = promise
  },
  RESET_USMAN_STATE(state) {
    // Helper mutation to reset state if needed
    state.isUseUsmanPermissions = undefined
    state.usmanPermissionsMap = {}
    state.isLoading = false
    state.isInitialized = false
    state.userRole = null
    state._initPromise = null
  },
}

// Actions
export const actions = {
  async fetchUsmanConfig({ commit, state }) {
    // If already initialized, return immediately
    if (state.isInitialized) {
      return true
    }

    // If there's already an ongoing initialization, wait for it
    if (state._initPromise) {
      return await state._initPromise
    }

    // Create the initialization promise
    const initPromise = (async () => {
      commit('SET_LOADING', true)
      try {
        // Fetch CRS permissions from the correct API endpoint
        const response = await resource.get(endpoint.v1.user.crsPermission)

        // Extract the nested data from the response
        const responseData = response.data?.data || response.data

        commit('SET_USMAN_PERMISSIONS', responseData.permissions)
        commit(
          'SET_USE_USMAN_PERMISSIONS',
          responseData.is_use_usman_permissions
        )
        return true
      } catch (error) {
        // Handle error gracefully
        commit('SET_USE_USMAN_PERMISSIONS', false)
        commit('SET_USMAN_PERMISSIONS', [])
        return false
      } finally {
        commit('SET_LOADING', false)
        commit('SET_INIT_PROMISE', null) // Clear the promise
      }
    })()

    // Store the promise to prevent concurrent calls
    commit('SET_INIT_PROMISE', initPromise)

    return await initPromise
  },

  setUserRole({ commit }, role) {
    commit('SET_USER_ROLE', role)
  },
}
